import React from "react";
import type { Theme } from "@mui/material";
import type { MovementServiceFormData } from "@features/vessel-call/vessel-call-status/MovementServices/schema/movementServiceFormSchema.ts";
import type { MovementRequestPayload } from "@services/movement-services/types.ts";
import type { FromToLocation } from "@services/vessel-call/types.ts";
import z from "zod";
import type { AssociatedPortService } from "../preview/types";

export type MovementError = {
  message?: string;
  name?: string;
  stack?: string;
  code?: string;
  status?: number;
  config?: {
    baseURL?: string;
    url?: string;
    method?: string;
    withCredentials?: boolean;
    timeout?: number;
    headers?: Record<string, string>;
    data?: string;
    allowAbsoluteUrls?: boolean;
    [key: string]: unknown;
  };
  response?: {
    status?: number;
    data?: {
      detail?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
};

export type MovementLocationOption = {
  value: string;
  label: string;
  moveType: string;
};

export type SelectOption = {
  value: string;
  label: string;
};

export type VesselCondition = {
  conditionCode: string;
  conditionDesc: string;
};

export type MovementReason = {
  movementReasonDesc: string;
};

export type ResourceType = {
  resourceTypeCode: string;
  resourceTypeDesc: string;
  active: boolean;
  costPerHr: number;
  resourceClassificationCode: string;
  departmentCode: string;
};

export type Resource = {
  resourceCode: string;
  resourceName: string;
  resourceTypeCode: string;
  startTime?: string;
  endTime?: string;
  active: boolean;
};

type ResourceWithStartTime = {
  startTimeOfUsage?: string | Date | null;
};

export const getEarliestResourceStartTime = (
  resources: ResourceWithStartTime[] | null | undefined,
): Date | null => {
  const startTimes = (resources ?? [])
    .map(({ startTimeOfUsage }) => {
      if (!startTimeOfUsage) return null;

      const date = new Date(startTimeOfUsage);
      return isNaN(date.getTime()) ? null : date;
    })
    .filter((date): date is Date => date !== null);

  if (!startTimes.length) return null;

  return new Date(Math.min(...startTimes.map((date) => date.getTime())));
};

export type PilotStationType = {
  code: string;
  name: string;
  active: boolean;
};

export const mapFromToLocationsToMovementOptions = (
  fromToLocations: FromToLocation[] = [],
): MovementLocationOption[] =>
  fromToLocations.map(({ fromToLocationCode, locationName, location }) => ({
    value: fromToLocationCode,
    label: locationName,
    moveType: location === "ANCH" ? "ANCHORAGE" : location,
  }));

export const mapVesselConditionsToOptions = (
  vesselConditions: VesselCondition[] = [],
): SelectOption[] =>
  vesselConditions.map(({ conditionCode, conditionDesc }) => ({
    value: conditionCode,
    label: conditionDesc,
  }));

export const mapMovementReasonsToOptions = (
  movementReasons: MovementReason[] = [],
): SelectOption[] =>
  movementReasons.map(({ movementReasonDesc }) => ({
    value: movementReasonDesc,
    label: movementReasonDesc,
  }));

export const mapAssociatedPortServiceToOptions = (
  associatedPortService: AssociatedPortService[] = [],
): SelectOption[] =>
  associatedPortService.map(({ serviceRequestNumber, vesselName, portServiceId }) => ({
    value: portServiceId,
    label: `${serviceRequestNumber} - (${vesselName})`,
  }));

export const mapResourceTypesToOptions = (resourceTypes: ResourceType[] = []): SelectOption[] =>
  resourceTypes.map(({ resourceTypeCode, resourceTypeDesc }) => ({
    value: resourceTypeCode,
    label: resourceTypeDesc,
  }));

export const mapResourcesToOptions = (resources: Resource[] = []): SelectOption[] =>
  resources
    .filter((resource) => resource.active)
    .map(({ resourceCode, resourceName }) => ({
      value: resourceCode,
      label: resourceName,
    }));

export const mapPilotStatusToOptions = (pilotStations: PilotStationType[] = []): SelectOption[] =>
  pilotStations.map(({ code, name }) => ({
    value: code,
    label: name,
  }));

export const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "pdf",
  "png",
  "gif",
  "bmp",
  "xls",
  "xlsx",
  "doc",
  "docx",
];

export const TEXTAREA_MAX_CHARS = 1000;

export const getMoveTypeForLocation = (value: string, locations: MovementLocationOption[]) => {
  const locationEntry = locations.find((location) => location.value === value);
  return locationEntry?.moveType ?? value;
};

export const createMovementType = (from: string, to: string) => `${from}_TO_${to}`;

const sanitizeAdditionalResources = (
  resources: MovementServiceFormData["additionalResources"],
): MovementRequestPayload["additionalResources"] => {
  if (!resources) return undefined;

  const normalized = resources
    .map((resource) => {
      const type = resource.additionalResourceType?.trim();
      const parsedCount = resource.numberOfResources
        ? Number(resource.numberOfResources)
        : Number.NaN;

      if (!type || Number.isNaN(parsedCount)) {
        return null;
      }

      return {
        additionalResourceType: type,
        numberOfResources: parsedCount,
      };
    })
    .filter(
      (entry): entry is { additionalResourceType: string; numberOfResources: number } => !!entry,
    );

  return normalized.length ? normalized : undefined;
};

export const mapMovementFormDataToPayload = (
  data: MovementServiceFormData,
): MovementRequestPayload => {
  const formattedRts = new Date(data.rts!).toISOString();
  const formattedEts = data.ets ? new Date(data.ets).toISOString() : undefined;

  return {
    movementType: data.movementType,
    from: data.from,
    fromAreaBerth: data.fromAreaBerth,
    to: data.to,
    toAreaBerth: data.toAreaBerth,
    ...(["ANCHORAGE_TO_ANCHORAGE", "BERTH_TO_ANCHORAGE"].includes(data.movementType) && {
      preferredAnchorageArea: data.preferredAnchorageArea || undefined,
      anchoragePreferenceReason: data.anchoragePreferenceReason?.trim() || undefined,
    }),
    rts: formattedRts,
    ets: formattedEts,
    vesselCondition: data.vesselCondition,
    movementReason: data.movementReason,
    associatedPortServiceId: data.associatedPortServiceId || undefined,
    draftFore: data.draftFore ? Number(data.draftFore) : null,
    draftAft: data.draftAft ? Number(data.draftAft) : null,
    hasProtrusionsOrOverhand: data.hasProtrusionsOrOverhand,
    hasOilStains: data.hasOilStains,
    additionalResources: sanitizeAdditionalResources(data.additionalResources),
    pilotRequestNotification: data.pilotRequestNotification?.map((doc) => ({
      type: doc.certificateType,
      url: doc.url,
      originalFileName: doc.name || undefined,
    })),
    h2s: data.h2s?.map((doc) => ({
      type: doc.certificateType,
      url: doc.url,
      originalFileName: doc.name || undefined,
    })),
    additionalDocuments: data.additionalDocuments?.map((doc) => {
      const normalizedDocumentType = doc.documentType?.trim() ?? "";
      const shouldKeepSpecify = normalizedDocumentType.toUpperCase() === "OTHER_SPECIFY";
      const toDateOnlyIso = (date: Date) =>
        new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();

      return {
        originalFileName: doc.name,
        url: doc.url,
        fileformat: doc.type ?? "",
        type: normalizedDocumentType,
        specify: shouldKeepSpecify ? (doc.otherSpecifyType?.trim() ?? "") : "",
        name: doc.documentName?.trim() ?? "",
        issueDate:
          doc.issueDate instanceof Date ? toDateOnlyIso(doc.issueDate) : (doc.issueDate ?? null),
        expiryDate:
          doc.expiryDate instanceof Date ? toDateOnlyIso(doc.expiryDate) : (doc.expiryDate ?? null),
        summary: doc.documentSummary?.trim() ?? "",
      };
    }),
    comments: data.comments,
    vesselId: data.vesselId,
    vesselCallId: data.vesselCallId,
  };
};

export const isPrintableKey = (e: React.KeyboardEvent) =>
  e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

export type UploadEntry = File | string | { name?: string };

export const getFileSignature = (entry: UploadEntry | undefined) => {
  if (!(entry instanceof File)) return null;
  return `${entry.name}-${entry.lastModified}-${entry.size}`;
};

export const triggerUpload = (
  entry: UploadEntry | undefined,
  handler: ((file: File) => void) | undefined,
  tracker: React.MutableRefObject<string | null>,
) => {
  if (!handler) return;
  if (!(entry instanceof File)) return;
  const signature = getFileSignature(entry);
  if (!signature) return;
  if (tracker.current === signature) return;
  tracker.current = signature;
  handler(entry);
};

export const makeCancelReasonSchema = (t: (key: string, opts?: any) => string) =>
  z.object({
    cancelReason: z
      .string()
      .min(
        1,
        t("form-validation.required", {
          label: t("vessel-management.vessel-status-page.cancelReason"),
        }),
      )
      .max(
        TEXTAREA_MAX_CHARS,
        t("form-validation.max-length", {
          label: t("vessel-management.vessel-status-page.cancelReason"),
          max: TEXTAREA_MAX_CHARS,
        }),
      ),
  });

export const makeInputSlotProps = (theme: Theme) => ({
  root: {
    className: `rounded-lg text-sm px-4 py-2 border`,
    style: {
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      color: theme.palette.text.primary,
    },
  },
  htmlInput: {
    maxLength: TEXTAREA_MAX_CHARS,
  },
});

export const makeInputSxProps = (theme: Theme) => ({
  "& .MuiInputBase-root": {
    borderRadius: "8px",
    borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
    padding: "12px",
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.text.primary,
  },
  "& textarea": {
    padding: 0,
  },
});
